import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Clients, useMutation } from "../apollo";
import { AWSRegions } from "./../generated/ups/graphql";
import { AuthRefreshToken } from "../graphql/mutations/authRefreshToken";
import {
  AuthRefreshTokenMutationResponse,
  AuthRefreshTokenMutationVariables,
} from "../types/user";
import {
  deleteLoginCookies,
  getIdToken,
  getRefreshToken,
  getUserLookupData,
  setLoginCookies,
} from "../utils/cookie";
import { RoutesPage } from "../constants/routes";

type TokenPayload = {
  "cognito:username": string;
  email: string;
  region: AWSRegions;
};

export const useGetCurrentUser = () => {
  const [user, setUser] = useState<TokenPayload | null>(null);
  const navigate = useNavigate();
  const [authRefreshToken] = useMutation<
    AuthRefreshTokenMutationResponse,
    AuthRefreshTokenMutationVariables
  >(Clients.Authn, AuthRefreshToken);

  const refreshToken = getRefreshToken();
  const { region = "" } = getUserLookupData();

  const decodeAndSetUser = useCallback((token: string) => {
    try {
      const decoded = jwtDecode<TokenPayload>(token);

      setUser(decoded);
    } catch (e) {
      console.error("Failed to decode token:", e);
    }
  }, []);

  const refreshAndSetUser = useCallback(async () => {
    try {
      if (!refreshToken) {
        return null;
      }

      const result = await authRefreshToken({
        variables: { refreshToken, region },
      });

      const tokens = result?.data?.authRefreshToken;

      if (tokens?.idToken && tokens?.accessToken) {
        setLoginCookies(tokens, window.location.hostname);
        decodeAndSetUser(tokens.idToken);
      } else {
        deleteLoginCookies(window.location.hostname);
        navigate(RoutesPage.Login, { replace: true });
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
    }
  }, [authRefreshToken, decodeAndSetUser, navigate, refreshToken, region]);

  useEffect(() => {
    const idToken = getIdToken();

    if (idToken) {
      decodeAndSetUser(idToken);
    } else if (refreshToken) {
      refreshAndSetUser();
    }
  }, [refreshToken, region, refreshAndSetUser, decodeAndSetUser]);

  return user
    ? {
        username: user["cognito:username"],
        email: user.email,
        region: user.region,
      }
    : null;
};
